import React from 'react';

import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useNavigationLayout from '~/hooks/useNavigationLayout';
import useViewportType from '~/hooks/useViewportType';
import {
	isPhase3,
} from '~/utilities/viewportSize';

/**
 * Hook to handle navigation link clicks with menu expansion
 *
 * When a navigation link is clicked in Phase 2 (mobile/responsive) with new navigation active,
 * expands the navigation to Phase 3 (desktop) to show labels.
 *
 * @param originalOnClick - Optional original onClick handler
 * @returns Enhanced onClick handler that expands menu if in Phase 2 and new navigation is active
 */
function useNavigationLinkClick(
	originalOnClick?: () => void,
): () => void {
	const viewportType = useViewportType();
	const [
		currentLayout,
	] = useNavigationLayout();
	const [
		isMenuExpanded,
		setMenuExpanded,
		isMenuMinimized,
	] = useMenuExpandedState();

	// Only expand if new navigation is active
	const isNewNavigation = currentLayout === 'new';
	// Check effective phase (considering menu expansion/minimization)
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);
	const isCurrentlyPhase2 = !effectivePhase3;

	return React.useCallback(
		() => {
			// If we're in Phase 2 (effective) and new navigation is active, expand navigation to Phase 3
			if (isCurrentlyPhase2 && isNewNavigation) {
				setMenuExpanded(true);
			}

			// Call original onClick handler if provided
			if (originalOnClick) {
				originalOnClick();
			}
		},
		[isCurrentlyPhase2, isNewNavigation, originalOnClick, setMenuExpanded],
	);
}

export default useNavigationLinkClick;
